import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import _ from "lodash";
import React, { useCallback, useMemo } from "react";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useGetEnglishLanguageName } from "src/hooks/useGetEnglishLanguageName";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import { useWeglot } from "src/plugins/weglot/hooks";
import * as OrgService from "src/services/orgService";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import { z } from "zod";

export const Form: React.FC = () => {
  const isAvelaAdmin = useRequiredHasuraRoles([HasuraRole.ADMIN]);
  const weglot = useWeglot();
  const renderDropdownItem = (item: LanguageMultiSelect) => {
    return (
      <Box>
        <span>{item.name}</span>
      </Box>
    );
  };

  const getEnglishLanguageName = useGetEnglishLanguageName({
    includeLocalName: true,
  });

  const formik = useFormikContext<FormikType>();

  const handleChangeLanguages = (values: LanguageMultiSelect[]) => {
    const languages = transformMultiSelectToLanguage(values);
    formik.setFieldValue("translation.languages", languages);
  };

  const options = useMemo(() => {
    if (!weglot?.options.languages) return [];

    const languages = [
      "en",
      ...weglot.options.languages.map((l) => l.language_to),
    ];
    return transformLanguageToMultiSelect(languages, getEnglishLanguageName);
  }, [getEnglishLanguageName, weglot]);

  const multiSelectInitialValues: LanguageMultiSelect[] = useMemo(() => {
    if (weglot === undefined) {
      return [];
    }
    return transformLanguageToMultiSelect(
      formik.values.translation.languages,
      getEnglishLanguageName
    );
  }, [formik.values.translation.languages, getEnglishLanguageName, weglot]);

  return (
    <Flex direction="column" gap={2}>
      <Heading as="h3" variant="admin">
        Languages
      </Heading>
      <FormikSwitchableContent<"enabled" | "disabled">
        name="translation.status"
        label="Translate into different languages"
        description="Enables translation for the Parent portal into multiple languages."
        checked={["enabled", ""]}
        unchecked={["disabled", ""]}
        showContent={(value): boolean => value === "enabled"}
        isDisabled={!isAvelaAdmin}
      >
        <FormControl
          isInvalid={!_.isEmpty(formik.errors.translation?.languages)}
        >
          <FormLabel>
            Translate to
            <MultiSelectInput<LanguageMultiSelect>
              size="md"
              onChange={handleChangeLanguages}
              options={options}
              initialValues={multiSelectInitialValues}
              renderDropdownItem={renderDropdownItem}
              labelAttr="name"
              isDisabled={!isAvelaAdmin}
            />
          </FormLabel>
          {formik.errors.translation?.languages && (
            <FormErrorMessage>
              {formik.errors.translation.languages}
            </FormErrorMessage>
          )}
        </FormControl>
      </FormikSwitchableContent>
    </Flex>
  );
};

export function useService(): {
  initialValues: RD.RemoteData<Error, FormikType>;
  submit: (values: FormikType) => Promise<void>;
} {
  const { remoteData, update } =
    OrgService.useOrgConfig<OrgConfig.TranslationOptionsConfig>(
      "TranslationOptions"
    );

  const initialValues = useMemo(() => {
    return remoteData.map((config): FormikType => {
      if (config === null || config?.disabled === true) {
        return {
          translation: {
            status: "disabled",
            languages: [
              ...OrgConfig.TranslationOptionsConfigSchema.default.languages,
            ],
          },
        };
      }

      return {
        translation: {
          status: "enabled",
          languages: config.languages,
        },
      };
    });
  }, [remoteData]);

  const submit = useCallback(
    async (values: FormikType) => {
      if (values.translation.status === "enabled") {
        // translation is enabled
        await update({
          disabled: false,
          languages: values.translation.languages,
        });
      } else {
        // translation is disabled, fall back to default language
        await update({
          disabled: true,
          languages: [
            ...OrgConfig.TranslationOptionsConfigSchema.default.languages,
          ],
        });
      }
    },
    [update]
  );

  return { initialValues, submit };
}

export const FormikSchema = z.object({
  translation: z.discriminatedUnion("status", [
    z.object({
      status: z.literal("enabled"),
      languages: z.array(z.string()).min(1, "This field is required"),
    }),
    z.object({
      status: z.literal("disabled"),
      languages: z.array(z.string()),
    }),
  ]),
});

export type FormikType = z.infer<typeof FormikSchema>;

/**
 * Helper functions
 */
type LanguageMultiSelect = {
  id: string;
  name: string;
};

const transformLanguageToMultiSelect = (
  languageCodes: string[],
  getLanguageName: (code: string) => string
): LanguageMultiSelect[] => {
  return languageCodes.map((languageCode) => {
    return {
      id: languageCode,
      name: `${getLanguageName(languageCode)}`,
    };
  });
};

const transformMultiSelectToLanguage = (
  languages: LanguageMultiSelect[]
): string[] => {
  return languages.map((language) => language.id);
};
